import React, { useState, useEffect } from 'react';
import { Monitor, Trash2, RefreshCw, Check, Loader, Tv, Tablet, Users, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { apiClient } from '../../utils/api';
import { parseServerTime } from '../../utils/sharedDevice';
import { StepUpCodeForm } from '../sharedDevice/StepUpCodeForm';

// Platform-paired devices (TVs, kiosks using the new platform pairing flow)
interface PlatformDevice {
  id: string;
  code: string;
  device_type: string;
  device_name: string;
  status: string;
  created_at: string;
  approved_at?: string;
  // HP Phoenix 6.0.163+: trusted family devices
  scope?: 'display' | 'family';
  is_active?: boolean;
  approved_by_name?: string | null;
  trust_expires_at?: string | null;
  last_seen_at?: string | null;
}


export const KioskDeviceManager: React.FC = () => {
  const [platformDevices, setPlatformDevices] = useState<PlatformDevice[]>([]);

  // Platform device pairing state
  const [pairingDigits, setPairingDigits] = useState(['', '', '', '', '', '']);
  const [pairingCode, setPairingCode] = useState('');
  const [pairingDeviceType, setPairingDeviceType] = useState<'kiosk' | 'tv'>('kiosk');
  const [approving, setApproving] = useState(false);
  const pairingInputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    loadPlatformDevices();
  }, []);

  // Load platform-paired devices (TVs, kiosks using new pairing flow)
  const loadPlatformDevices = async () => {
    try {
      const data = await apiClient.get('/api/platform/device/list');
      setPlatformDevices(data.devices || data || []);
    } catch (error: any) {
      console.error('Error loading devices:', error);
      toast.error('Failed to load devices');
    }
  };

  // Approve a device pairing code (from TV or kiosk)
  const approvePairingCode = async () => {
    if (!pairingCode.trim()) {
      toast.error('Enter a pairing code');
      return;
    }

    setApproving(true);
    try {
      const prefix = pairingDeviceType === 'tv' ? 'TV' : 'K';
      const fullCode = `${prefix}-${pairingCode.toUpperCase().trim()}`;
      const data = await apiClient.post('/api/platform/device/approve-code', {
        code: fullCode
      });

      toast.success(data.message || 'Device paired successfully!');
      setPairingCode('');
      loadPlatformDevices();
    } catch (error: any) {
      console.error('Error approving device:', error);
      toast.error(error?.response?.data?.error || 'Failed to approve device');
    } finally {
      setApproving(false);
    }
  };

  // Revoke a platform-paired device
  const revokePlatformDevice = async (deviceId: string, deviceName: string) => {
    if (!confirm(`Revoke access for "${deviceName}"? The device will need to be re-paired.`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/platform/device/${deviceId}`);

      toast.success('Device access revoked');
      loadPlatformDevices();
    } catch (error: any) {
      console.error('Error revoking device:', error);
      toast.error('Failed to revoke device access');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Devices</h2>
          <p className="text-gray-600">Shared TVs and tablets, kiosks, and display devices</p>
        </div>
        <button
          onClick={loadPlatformDevices}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Shared family devices pair themselves at /device; nothing is typed on them. */}
      <div className="mb-6 bg-white border border-indigo-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-50 rounded-lg">
            <Users className="w-8 h-8 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Add a TV or tablet</h3>
            <p className="text-gray-600 text-sm">
              In the web browser on the TV or tablet, open{' '}
              <span className="font-mono font-semibold text-gray-900">{window.location.host}/device</span>. It shows a
              code and a QR code. Scan the QR code with your phone, or enter the code at{' '}
              <a href="/approve" className="font-mono font-semibold text-indigo-700 hover:underline">{window.location.host}/approve</a>.
              Anyone at that screen can then tap their picture to use HousePoints; parent actions there need approval
              from a parent's phone.
            </p>
          </div>
        </div>
      </div>

      {/* Step-up fallback: a shared device asking for parent approval shows an S- code. */}
      <div className="mb-8 bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <ShieldCheck className="w-8 h-8 text-gray-700" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Approve a request from a shared device</h3>
            <p className="text-gray-600 text-sm mb-4">
              When a shared TV or tablet asks for a parent's approval, enter the code it shows to give it parent
              access for 10 minutes.
            </p>
            <StepUpCodeForm />
          </div>
        </div>
      </div>

      {/* Pair TV or Kiosk - New Platform Pairing Flow */}
      <div className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white rounded-lg shadow-sm">
            <Tv className="w-8 h-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Pair a kiosk or the TV app</h3>
            <p className="text-gray-600 text-sm mb-4">
              Enter the pairing code shown on your TV or device screen
            </p>
            <div className="space-y-5">
              {/* Device type toggle */}
              <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-100">
                <button
                  onClick={() => setPairingDeviceType('kiosk')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    pairingDeviceType === 'kiosk'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Tablet className="w-4 h-4" />
                  Kiosk
                </button>
                <button
                  onClick={() => setPairingDeviceType('tv')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    pairingDeviceType === 'tv'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Tv className="w-4 h-4" />
                  TV
                </button>
              </div>

              {/* Code input + button row */}
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <span className="text-lg font-mono font-bold text-gray-400 mr-1">{pairingDeviceType === 'tv' ? 'TV' : 'K'}-</span>
                  {pairingDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (pairingInputRefs.current[index] = el)}
                      type="text"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => {
                        let val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                        if (val.length > 1) {
                          val = val.replace(/^(K|TV|D)-?/, '');
                          const chars = val.slice(0, 6).split('');
                          const newDigits = ['', '', '', '', '', ''];
                          chars.forEach((c, i) => { if (i < 6) newDigits[i] = c; });
                          setPairingDigits(newDigits);
                          setPairingCode(newDigits.join(''));
                          pairingInputRefs.current[Math.min(chars.length, 5)]?.focus();
                        } else {
                          const newDigits = [...pairingDigits];
                          newDigits[index] = val;
                          setPairingDigits(newDigits);
                          setPairingCode(newDigits.join(''));
                          if (val && index < 5) pairingInputRefs.current[index + 1]?.focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !pairingDigits[index] && index > 0) {
                          pairingInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'Enter' && pairingDigits.every(d => d !== '')) {
                          approvePairingCode();
                        }
                      }}
                      disabled={approving}
                      className="w-10 h-12 text-xl font-bold text-center rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 disabled:opacity-50 uppercase font-mono"
                    />
                  ))}
                </div>
                <button
                  onClick={approvePairingCode}
                  disabled={approving || pairingDigits.some(d => d === '')}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                >
                  {approving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Pairing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Pair Device
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Paired devices */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Tv className="w-5 h-5 text-blue-600" />
          Paired devices ({platformDevices.length})
        </h3>
        {platformDevices.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Monitor className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No paired devices yet</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {platformDevices.map(device => (
              <PlatformDeviceCard
                key={device.id}
                device={device}
                onRevoke={() => revokePlatformDevice(device.id, device.device_name)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Platform Device Card Component (for TVs and new pairing flow)
const PlatformDeviceCard: React.FC<{
  device: PlatformDevice;
  onRevoke?: () => void;
}> = ({ device, onRevoke }) => {
  const getDeviceIcon = () => {
    switch (device.device_type) {
      case 'tv':
        return Tv;
      case 'kiosk':
        return Tablet;
      case 'web':
        return Users;
      default:
        return Monitor;
    }
  };

  const DeviceIcon = getDeviceIcon();
  const isShared = device.scope === 'family';
  const isActive = device.is_active !== false && device.status !== 'expired';
  const approvedAt = parseServerTime(device.approved_at);
  const trustExpiresAt = parseServerTime(device.trust_expires_at);
  const lastSeenAt = parseServerTime(device.last_seen_at);

  return (
    <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-3 rounded-lg bg-blue-100">
            <DeviceIcon className="w-6 h-6 text-blue-600" />
          </div>

          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">{device.device_name}</h4>
            <div className="flex gap-2 mt-1 flex-wrap">
              <span
                className={`text-xs px-2 py-1 rounded font-medium ${
                  isShared ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {isShared ? 'Shared family device' : 'Display'}
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-mono">
                {device.code}
              </span>
              {isActive ? (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Paired</span>
              ) : (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Expired</span>
              )}
            </div>

            <dl className="mt-2 text-sm text-gray-600 grid sm:grid-cols-2 gap-x-6 gap-y-1">
              <div>
                <dt className="inline">Approved: </dt>
                <dd className="inline text-gray-900">
                  {approvedAt ? formatDistanceToNow(approvedAt, { addSuffix: true }) : 'Unknown'}
                  {device.approved_by_name ? ` by ${device.approved_by_name}` : ''}
                </dd>
              </div>
              <div>
                <dt className="inline">{isActive ? 'Trusted until: ' : 'Trust ended: '}</dt>
                <dd className="inline text-gray-900">
                  {trustExpiresAt
                    ? trustExpiresAt.toLocaleDateString(undefined, { dateStyle: 'medium' })
                    : 'Until removed'}
                </dd>
              </div>
              <div>
                <dt className="inline">Last seen: </dt>
                <dd className="inline text-gray-900">
                  {lastSeenAt ? formatDistanceToNow(lastSeenAt, { addSuffix: true }) : 'Not yet'}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {onRevoke && (
          <button
            onClick={onRevoke}
            className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Revoke device access"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default KioskDeviceManager;
